import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useActiveBanner } from "@/hooks/useActiveBanner";
import { useLanguageContext } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

export function AnnouncementBanner() {
  const { data: banner, isLoading } = useActiveBanner();
  const { isRTL } = useLanguageContext();
  const [isDismissed, setIsDismissed] = useState(false);

  // Charger l'état dismissed au mount / quand le bandeau change
  useEffect(() => {
    if (!banner) return;
    const key = `hirafi_banner_dismissed_${banner.id}`;
    setIsDismissed(localStorage.getItem(key) === "true");
  }, [banner?.id]);

  if (isLoading || !banner || isDismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(`hirafi_banner_dismissed_${banner.id}`, "true");
    setIsDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label={isRTL ? "إعلان خاص" : "Annonce spéciale"}
      dir={isRTL ? "rtl" : "ltr"}
      className={cn(
        "w-full px-4 py-3 sm:px-6",
        "flex items-center gap-3 sm:gap-4",
        "animate-fade-in"
      )}
      style={{
        backgroundColor: banner.backgroundColor,
        color: banner.textColor,
      }}
    >
      {/* Image / logo optionnel */}
      {banner.imageUrl && (
        <img
          src={banner.imageUrl}
          alt={banner.imageAlt ?? ""}
          className="h-8 w-8 flex-shrink-0 object-contain sm:h-10 sm:w-10"
        />
      )}

      {/* Texte */}
      <div className="flex-1 min-w-0">
        <span className="text-sm font-display font-semibold leading-tight block sm:inline">
          {banner.title}
        </span>
        {banner.subtitle && (
          <span className="text-xs opacity-90 block sm:inline sm:ms-2">
            {banner.subtitle}
          </span>
        )}
      </div>

      {/* CTA + fermeture */}
      <div className="flex flex-shrink-0 items-center gap-2">
        {banner.ctaUrl && banner.ctaText && (
          <a
            href={banner.ctaUrl}
            target={banner.ctaTarget}
            rel={banner.ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
            className="whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-opacity hover:opacity-80 sm:px-4 sm:py-2 sm:text-sm"
            style={{
              backgroundColor: banner.textColor,
              color: banner.backgroundColor,
            }}
          >
            {banner.ctaText}
          </a>
        )}

        {banner.dismissible && (
          <button
            onClick={handleDismiss}
            aria-label={isRTL ? "إغلاق الإعلان" : "Fermer l'annonce"}
            className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-black/10"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
